import type { ReactNode } from 'react'
import type { SystemStyleObject } from '@chakra-ui/react'
import { chakra, defineSlotRecipe, useSlotRecipe } from '@chakra-ui/react'
import { Button } from 'components/ui/button'
import { Chip } from 'components/ui/chip'
import { Pressable } from '@ayble-health/dashboard-components'
import { SYMPTOMS_FEEDBACK_META, type SymptomsFeedback } from '../../models/careBlockDetail'
import {
  PhasesIcon, ExclamationIcon, CheckCircleIcon,
  CalendarIcon, MoreVerticalIcon, ClipboardIcon, PaperclipIcon,
  AwardIcon, AlertTriangleIcon,
  PlusIcon, CloseIcon,
} from './icons'

// C_Card/Kanban (Figma — Design System 2.1, node 40005215:8417)
// Spec: docs/design/C_care-kanban-design-spec.md
// Notes addon spec:      §14 (Figma 40005215:8573)
// Engagement addon spec: §16 (Figma 40005215:8528)
//
// Recipe is co-located here per the repo's ui convention (theme owns tokens,
// components own visual shapes). Token refs use bare names; borders use the
// `border: '1px solid'` + `borderColor` shorthand to dodge the v3
// three-prop → `border: solid` compilation bug.

const laneBlockCardRecipe = defineSlotRecipe({
  className: 'careos-lane-block-card',
  slots: ['root', 'header', 'body', 'notesAddon', 'progressAddon', 'engagementAddon'],

  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 'card',
      background: 'canvas',
      overflow: 'hidden',
      fontFamily: 'body',
      width: '100%',
    },
    header: {
      width: '100%',
      padding: '10px 12px 8px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    notesAddon: {
      width: '100%',
      borderTop: '1px solid',
      borderTopColor: 'cloud',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
    },
    progressAddon: {
      width: '100%',
      padding: '0 10px 10px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: 'bluegrey',
    },
    engagementAddon: {
      width: '100%',
      borderTop: '1px solid',
      borderTopColor: 'cloud',
      padding: '8px 10px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
  },

  variants: {
    variant: {
      suggested: {
        root: { border: '2px solid', borderColor: 'aqua' },
        header: { background: 'aqua', color: 'white' },
        body: { padding: '10px 8px 8px', gap: '16px' },
      },
      'in-progress': {
        root: { border: '1px solid', borderColor: 'cloud' },
        body: { padding: '10px 6px 16px 10px', gap: '8px' },
      },
      overdue: {
        root: { border: '1px solid', borderColor: 'cloud' },
        header: { background: 'shortbread', color: 'seafoam' },
        body: { padding: '10px 6px 16px 10px', gap: '8px' },
      },
      error: {
        root: { border: '1px solid', borderColor: 'cloud' },
        header: { background: 'fuschia', color: 'white' },
        body: { padding: '10px 6px 16px 10px', gap: '8px' },
      },
      completed: {
        root: { border: '1px solid', borderColor: 'cloud' },
        header: { background: 'mint', color: 'seafoam' },
        body: { padding: '10px 8px 8px', gap: '8px' },
      },
      archived: {
        root: { border: '1px solid', borderColor: 'cloud' },
        body: { padding: '10px 6px 16px 10px', gap: '8px' },
      },
    },
  },

  defaultVariants: {
    variant: 'in-progress',
  },
})

// ─── Variant types ────────────────────────────────────────────────────────

export type LaneBlockStatus =
  | 'suggested'
  | 'in-progress'
  | 'overdue'
  | 'error'
  | 'completed'
  | 'archived'

export type NotesVariant =
  | 'stacked'              // # Notes only
  | 'trouble-implementing' // # Notes + latest symptoms feedback + "+N"
  | 'out-of-range'         // # Attachments + "Out of Range"
  | 'in-range'             // # Attachments + "In Range"
  | 'no-change'            // # Attachments + "No Change"

export interface LaneBlockProgress {
  dueLabel: string // e.g. "Jan 7"
}

export interface LaneBlockNotes {
  variant: NotesVariant
  count: number
  /** Override label noun ("Notes" / "Attachments"). Optional — derived from variant by default. */
  noun?: string
  /** Trouble Implementing only — extra count for "+N" pill. */
  extraCount?: number
  /**
   * Date of the most recent note/attachment, `YYYY-MM-DD`. When set (with `asOf`)
   * it replaces the count label: "Added 2 days ago" within 30 days, then
   * "Added MM/DD/YYYY".
   */
  lastAddedOn?: string
  /**
   * The "today" that `lastAddedOn` is measured against, `YYYY-MM-DD`. Pinned by
   * the caller — prototype dates are literals, never derived from the clock.
   */
  asOf?: string
  /**
   * Trouble Implementing only — the block's most recent symptoms feedback.
   * The pill shows its form label ("No change", "Worsening", …), Mint when
   * positive and Ballet when negative. No pill until one has been recorded.
   */
  symptoms?: SymptomsFeedback
}

/** Engagement addon — bottom block for in-progress cards (§16). */
export interface LaneBlockEngagement {
  /** Free-form timestamp string, e.g. "Yesterday, 6:55 PM ET". */
  lastEngaged: string
  /** Numeric count for the bottom row. */
  count: number
  /**
   * When set, Row 2 renders in "of-total" mode: "{count} of {total} {countNoun} completed".
   * When omitted, Row 2 renders in default rolling-window mode: "{count} times {windowLabel}".
   */
  total?: number
  /** Noun for of-total mode, e.g. "Gut Checks". Required when `total` is set. */
  countNoun?: string
  /** Suffix for default mode. Defaults to "over last 30 days". Ignored when `total` is set. */
  windowLabel?: string
  /**
   * True if the last engagement was within the last 72 hours.
   * Drives the Row 1 dot color — Seafoam (recent) vs. Fuschia (lacking).
   * Caller computes this from the real timestamp. Defaults to `true`.
   */
  isRecent?: boolean
  /**
   * When set, Row 2 is **replaced** with a Fuschia alert-triangle + "Streak ended {days} days ago"
   * warning instead of the count/of-total phrase. Used when a habit task's streak has lapsed.
   * Takes precedence over `total` / `windowLabel`. See §16.3b.
   */
  streakEndedDays?: number
}

export interface LaneBlockCardProps {
  variant: LaneBlockStatus
  title: string
  /** "T. Haywood, MD" | "Suggested by Ayble" — sublabel under title. */
  assignedBy?: string
  /** Override the status header label (e.g. "14 days ago", "4 Triggers Found"). */
  headerLabel?: string
  progress?: LaneBlockProgress
  notes?: LaneBlockNotes
  engagement?: LaneBlockEngagement
  /** Completed only — render full-width "View Results" Text Outline button. */
  showViewResults?: boolean
  /** Suggested only — copy for the Feather stat callout. */
  statCallout?: ReactNode
  onSetup?: () => void
  onDismiss?: () => void
  onTrailingAction?: () => void
  /**
   * Opens the block's detail drawer. When provided the whole card becomes a
   * button — every nested control stops propagation so Setup / Dismiss /
   * View Results / Archive / the kebab keep their own behavior.
   */
  onOpen?: () => void
}

// ─── Header strip configuration ──────────────────────────────────────────
// HEADER_BG is kept only for the conditional render guard — bg/color live in the recipe.

const HEADER_BG: Partial<Record<LaneBlockStatus, string>> = {
  suggested: 'var(--aqua)',
  overdue:   'var(--shortbread)',
  error:     'var(--fuschia)',
  completed: 'var(--mint)',
}

const DEFAULT_HEADER_LABEL: Partial<Record<LaneBlockStatus, string>> = {
  suggested: 'Recommended Next',
  overdue:   '14 days ago',
  error:     'Error',
  completed: 'Results Uploaded',
}

const TRAILING_ACTION_LABEL: Partial<Record<LaneBlockStatus, string>> = {
  overdue:   'Nudge',
  error:     'Resolve',
  completed: 'Archive',
}

// careOS "text-outline" micro-button (28px, sm radius, seafoam, blue-grey
// border). No clean ui/Button mapping at this size — kept as a faithful
// co-located one-off (hybrid button strategy). `endIcon` renders trailing.
function TextOutlineButton({ children, endIcon, onClick, style }: {
  children: ReactNode
  endIcon?: ReactNode
  onClick?: () => void
  style?: React.CSSProperties
}) {
  return (
    <chakra.button
      type="button"
      // Every control inside the card stops propagation: the card itself opens
      // the detail drawer, and Dismiss / View Results must not.
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); onClick?.() }}
      className="hover-aqua-outline"
      css={{
        height: '28px',
        borderRadius: 'sm',
        background: 'transparent',
        // `seafoam`, not `positive`. `positive` is a Chip/Badge *variant* name,
        // not a colour token — Chakra drops an unknown token reference silently,
        // so this label had been rendering Chakra's default near-black
        // (#09090B) rather than any careOS colour at all.
        color: 'seafoam',
        border: '1px solid',
        borderColor: 'bluegrey',
        fontFamily: 'body',
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '0 16px',
        fontSize: 'var(--text-cos-btn-md-size)', fontWeight: 'var(--text-cos-btn-md-weight)', letterSpacing: '0.02em',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
      {endIcon && <span style={{ display: 'inline-flex' }}>{endIcon}</span>}
    </chakra.button>
  )
}

function HeaderIcon({ variant }: { variant: LaneBlockStatus }) {
  switch (variant) {
    case 'suggested': return <PhasesIcon />
    case 'overdue':
    case 'error':     return <ExclamationIcon />
    case 'completed': return <CheckCircleIcon />
    default:          return null
  }
}

function StatusHeader({ variant, label, trailingActionLabel, onTrailingAction, headerCss }: {
  variant: LaneBlockStatus
  label: string
  trailingActionLabel?: string
  onTrailingAction?: () => void
  headerCss?: SystemStyleObject
}) {
  // Only variants with a coloured strip render the header.
  if (!(variant in HEADER_BG)) return null
  return (
    <chakra.div css={headerCss} style={{ fontFamily: 'var(--font-body)' }}>
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
        <HeaderIcon variant={variant} />
      </span>
      <span style={{
        flex: 1, minWidth: 0,
        fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)', lineHeight: 'var(--text-sm-b-lh)', letterSpacing: '0.01em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {label}
      </span>
      {/* Suggested reserves an opacity-0 stub so header heights match across variants. */}
      {variant === 'suggested' ? (
        <span aria-hidden style={{ opacity: 0, fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', letterSpacing: '0.02em' }}>
          Preview
        </span>
      ) : trailingActionLabel ? (
        // careOS "text-only" → ui/Button link (clean map). Error has a Fuschia
        // header where the link's seafoam doesn't read; force canvas.
        <Button
          variant="link"
          size="xs"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onTrailingAction?.() }}
          style={variant === 'error' ? { color: 'var(--canvas)' } : undefined}
        >
          {trailingActionLabel}
        </Button>
      ) : null}
    </chakra.div>
  )
}

// ─── Progress addon ──────────────────────────────────────────────────────

function ProgressAddon({ dueLabel, progressCss }: LaneBlockProgress & { progressCss?: SystemStyleObject }) {
  return (
    <chakra.div css={progressCss} style={{ fontFamily: 'var(--font-body)' }}>
      <CalendarIcon />
      <span style={{ fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.01em' }}>{dueLabel}</span>
    </chakra.div>
  )
}

// ─── Notes addon (5 variants per §14) ────────────────────────────────────

const NOTES_NOUN: Record<NotesVariant, string> = {
  'stacked':              'Notes',
  'trouble-implementing': 'Notes',
  'out-of-range':         'Attachments',
  'in-range':             'Attachments',
  'no-change':            'Attachments',
}

// Whole days between two `YYYY-MM-DD` dates, parsed as UTC so DST never shifts the count.
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)
}

// "Added 2 days ago" up to 30 days; "Added 09/12/2026" from day 31.
function formatAdded(lastAddedOn: string, asOf: string): { prefix: string; when: string } {
  const days = daysBetween(lastAddedOn, asOf)
  if (days > 30) {
    const [y, m, d] = lastAddedOn.split('-')
    return { prefix: 'Added', when: `${m}/${d}/${y}` }
  }
  if (days <= 0) return { prefix: 'Added', when: 'today' }
  return { prefix: 'Added', when: days === 1 ? '1 day ago' : `${days} days ago` }
}

function NotesAddon({ variant, count, noun, extraCount, lastAddedOn, asOf, symptoms, notesCss }: LaneBlockNotes & { notesCss?: SystemStyleObject }) {
  const label = `${count} ${noun ?? NOTES_NOUN[variant]}`
  // When the last-added date is known it replaces the count entirely.
  const added = lastAddedOn && asOf ? formatAdded(lastAddedOn, asOf) : null
  const isAttachments = variant === 'out-of-range' || variant === 'in-range' || variant === 'no-change'
  // The symptoms pill and overflow counter move to a second row: on one line in
  // a lane-width card they squeezed the date down to an ellipsis.
  const hasPillRow = variant === 'trouble-implementing' && (!!symptoms || typeof extraCount === 'number')

  return (
    <chakra.div
      css={notesCss}
      style={{
        gap: variant === 'stacked' ? 8 : hasPillRow ? 6 : 4,
        fontFamily: 'var(--font-body)',
        ...(hasPillRow && { flexDirection: 'column', alignItems: 'stretch' }),
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', color: 'var(--seafoam)' }}>
          {isAttachments ? <PaperclipIcon /> : <ClipboardIcon />}
        </span>
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)', lineHeight: 'var(--text-xs-b-lh)', letterSpacing: '0.01em',
          color: 'var(--seafoam)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {added ? (
            <>
              <span style={{ fontWeight: 'var(--text-xs-weight)', color: 'var(--blue-grey)' }}>{added.prefix}</span>
              {' '}{added.when}
            </>
          ) : label}
        </span>
      </span>
      {hasPillRow && (
        // Indented by the icon (12) plus its gap (8) so the pills line up under the date.
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingLeft: 20 }}>
          {symptoms && (
            <Chip variant={SYMPTOMS_FEEDBACK_META[symptoms].valence === 'positive' ? 'mint' : 'ballet'}>
              {SYMPTOMS_FEEDBACK_META[symptoms].formLabel}
            </Chip>
          )}
          {typeof extraCount === 'number' && <Chip variant="neutral">+{extraCount}</Chip>}
        </span>
      )}
      {variant === 'out-of-range' && <Chip variant="ballet">Out of Range</Chip>}
      {variant === 'in-range'     && <Chip variant="mint">In Range</Chip>}
      {variant === 'no-change'    && <Chip variant="neutral">No Change</Chip>}
    </chakra.div>
  )
}

// ─── Engagement addon (§16) ──────────────────────────────────────────────
// Two-row block for in-progress cards: pulse icon + last-engaged timestamp,
// then award icon + "{count} times over last 30 days".

function EngagementAddon({ lastEngaged, count, total, countNoun, windowLabel, isRecent = true, streakEndedDays, engagementCss }: LaneBlockEngagement & { engagementCss?: SystemStyleObject }) {
  // lineHeight: 1 overrides the token's 1.25 so the text's line-box matches the
  // glyph height. Without this the centered text visually rides high next to the icon.
  const xsRegular: React.CSSProperties = {
    fontSize: 'var(--text-xs-size)', fontWeight: 'var(--text-xs-weight)', lineHeight: 1, letterSpacing: '0.01em',
    fontFamily: 'var(--font-body)',
  }
  const xsBold: React.CSSProperties = {
    fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)', lineHeight: 1, letterSpacing: '0.01em',
    fontFamily: 'var(--font-body)',
  }
  // Row 1 dot — Seafoam if engaged ≤ 72 h ago, Fuschia if beyond.
  const dotColor = isRecent ? 'var(--seafoam)' : 'var(--fuschia)'
  return (
    <chakra.div css={engagementCss}>
      {/* Row 1 — Last engaged */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <span
          aria-label={isRecent ? 'Recent engagement' : 'Engagement lacking'}
          style={{
            flexShrink: 0,
            width: 14, height: 14,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* 8.5 px — intentionally subtle. See §16.2a. */}
          <span style={{ width: 8.5, height: 8.5, borderRadius: '50%', background: dotColor }} />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
          <span style={{ ...xsRegular, color: 'var(--blue-grey)', flexShrink: 0 }}>Engaged</span>
          <span style={{ ...xsBold, color: 'var(--seafoam)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {lastEngaged}
          </span>
        </span>
      </div>
      {/* Row 2 — Streak-ended warning (if set) OR count phrase */}
      {typeof streakEndedDays === 'number' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--fuschia)' }}>
            <AlertTriangleIcon />
          </span>
          <span style={{ ...xsRegular, color: 'var(--seafoam)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Streak ended {streakEndedDays} days ago
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--seafoam)' }}>
            <AwardIcon />
          </span>
          <span style={{ ...xsRegular, color: 'var(--seafoam)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {typeof total === 'number'
              ? `${count} of ${total} ${countNoun ?? ''} completed`.replace(/\s+/g, ' ').trim()
              : `${count} times ${windowLabel ?? 'over last 30 days'}`}
          </span>
        </div>
      )}
    </chakra.div>
  )
}

// ─── Title row ───────────────────────────────────────────────────────────

function TitleRow({ title, assignedBy, trailing }: { title: string; assignedBy?: string; trailing?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, width: '100%' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span className="md-body" style={{
          color: 'var(--evergreen)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {title}
        </span>
        {assignedBy && (
          <span style={{
            fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.01em',
            color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {assignedBy}
          </span>
        )}
      </div>
      {trailing}
    </div>
  )
}

function Kebab() {
  return (
    <Pressable
      type="button"
      aria-label="Card actions"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      w="16px"
      h="16px"
      p={0}
      color="bluegrey"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
    >
      <MoreVerticalIcon />
    </Pressable>
  )
}

// ─── Top-level LaneBlockCard ────────────────────────────────────────────────

export function LaneBlockCard({
  variant, title, assignedBy, headerLabel,
  progress, notes, engagement,
  showViewResults, statCallout,
  onSetup, onDismiss, onTrailingAction, onOpen,
}: LaneBlockCardProps) {
  const slotRecipe = useSlotRecipe({ recipe: laneBlockCardRecipe })
  const styles = slotRecipe({ variant })

  const isSuggested = variant === 'suggested'
  const isCompleted = variant === 'completed'
  const isArchived  = variant === 'archived'

  return (
    <chakra.div
      css={styles.root}
      className={onOpen ? 'hover-shadow' : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open ${title} details` : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? (e: React.KeyboardEvent) => {
        // Space/Enter activate, matching a native button. Ignore keys that
        // bubbled up from a nested control (the kebab, Setup, …).
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
      } : undefined}
      style={onOpen ? { cursor: 'pointer' } : undefined}
    >
      <StatusHeader
        variant={variant}
        label={headerLabel ?? DEFAULT_HEADER_LABEL[variant] ?? ''}
        trailingActionLabel={TRAILING_ACTION_LABEL[variant]}
        onTrailingAction={onTrailingAction}
        headerCss={styles.header}
      />

      <chakra.div css={styles.body}>
        <TitleRow
          title={title}
          assignedBy={assignedBy}
          trailing={
            isArchived ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Chip variant="canvas">Archived</Chip>
                <Kebab />
              </span>
            ) : (
              <Kebab />
            )
          }
        />

        {isSuggested && (
          <>
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              {/* careOS 28px seafoam micro-button (primary, overridden) — one-off. */}
              <chakra.button
                type="button"
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onSetup?.() }}
                css={{ color: 'white', border: 'none', borderRadius: 'sm', fontFamily: 'body' }}
                style={{
                  flex: 1, height: 28,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '0 12px',
                  background: 'var(--seafoam)',
                  fontSize: 'var(--text-cos-btn-md-size)', fontWeight: 'var(--text-cos-btn-md-weight)', letterSpacing: '0.02em',
                  cursor: 'pointer',
                }}
              >
                Setup
                <span style={{ display: 'inline-flex' }}><PlusIcon /></span>
              </chakra.button>
              <TextOutlineButton onClick={onDismiss} endIcon={<CloseIcon />} style={{ flex: 1 }}>
                Dismiss
              </TextOutlineButton>
            </div>
            {statCallout && (
              <chakra.div
                css={{
                  width: '100%',
                  // Same slip as TextOutlineButton, twice over: `neutral` and
                  // `positive` are Chip variant names. Neither resolves as a
                  // colour, so the callout lost its Feather container entirely
                  // — transparent on white — and took Chakra's near-black text.
                  // The prop doc has always said "Feather stat callout".
                  background: 'feather',
                  borderRadius: 'sm',
                  color: 'seafoam',
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.01em',
                }}
              >
                {statCallout}
              </chakra.div>
            )}
          </>
        )}

        {isCompleted && showViewResults && (
          <TextOutlineButton style={{ width: '100%' }}>View Results</TextOutlineButton>
        )}
      </chakra.div>

      {/* Addon stack order — matches Figma: progress → engagement → notes (notes always last). */}
      {progress   && <ProgressAddon   {...progress}   progressCss={styles.progressAddon} />}
      {engagement && <EngagementAddon {...engagement} engagementCss={styles.engagementAddon} />}
      {notes      && <NotesAddon      {...notes}      notesCss={styles.notesAddon} />}
    </chakra.div>
  )
}
